import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Award, ExternalLink, X, ChevronRight } from 'lucide-react';
import '../App.css';

const CertificatesBadgesCollage = () => {
  const [selectedItem, setSelectedItem] = useState(null);

  const items = [
    { id: 1, type: 'certificate', name: "AWS Certified Solutions Architect", issuer: "Amazon Web Services", date: "2023", image: "aws.png", link: "https://www.credly.com/badges/aws-certified-solutions-architect" },
    { id: 2, type: 'badge', name: "Certified Kubernetes Administrator", issuer: "CNCF", image: "newt.png", link: "https://www.credly.com/badges/certified-kubernetes-administrator" },
    { id: 3, type: 'certificate', name: "Google Cloud Professional Data Engineer", issuer: "Google Cloud", date: "2022", image: "aws.png", link: "https://www.credential.net/google-cloud-professional-data-engineer" },
    { id: 4, type: 'badge', name: "Oracle Certified Professional, Java SE 11 Developer", issuer: "Oracle", image: "newt.png", link: "https://catalog-education.oracle.com/pls/certview/sharebadge?id=oracle-certified-professional-java-se-11-developer" },
    { id: 5, type: 'certificate', name: "Microsoft Certified: Azure Developer Associate", issuer: "Microsoft", date: "2023", image: "framer.png", link: "https://www.credly.com/badges/microsoft-certified-azure-developer-associate" },
    { id: 6, type: 'badge', name: "Certified Scrum Master", issuer: "Scrum Alliance", image: "edu.png", link: "https://www.scrumalliance.org/community/profile/your-profile-id" },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1 }
  };

  return (
    <section className="py-8 sm:py-12 md:py-16 bg-black">
      <div className="container mx-auto px-4">
      <h2
  className="text-2xl sm:text-6xl font-bold text-center mb-12 text-white text-transparent bg-clip-text shadow-lg tracking-wide"
  style={{ fontFamily: "'Roboto Condensed', sans-serif" }}>Achievements Showcase</h2>

        <motion.div 
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {items.map((item) => (
            <motion.div
              key={item.id}
              className="relative group cursor-pointer"
              variants={itemVariants}
              whileHover={{ scale: 1.05, rotate: item.type === 'badge' ? 10 : 0 }}
              onClick={() => setSelectedItem(item)}
            >
              <img 
                src={item.image} 
                alt={item.name} 
                className={`w-full h-auto object-cover shadow-lg ${item.type === 'badge' ? 'rounded-full' : 'rounded-lg'}`}
              />
              <div className={`absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center ${item.type === 'badge' ? 'rounded-full' : 'rounded-lg'}`}>
                <p className="text-white text-center text-xs sm:text-sm px-1 sm:px-2">
                  {item.name}
                </p>
              </div>
              {item.type === 'badge' && (
                <motion.div
                  className="absolute -top-1 -right-1 sm:-top-2 sm:-right-2 bg-yellow-400 rounded-full p-1"
                  whileHover={{ scale: 1.2, rotate: 90 }}
                >
                  <Award className="text-gray-900" size={12} />
                </motion.div>
              )}
            </motion.div>
          ))}
        </motion.div>
        
        <div className="mt-8 sm:mt-12 text-center">
          <motion.a
            href="/certificates" // Replace with your actual certificates page URL
            className="inline-flex items-center text-blue-400 hover:text-blue-300 text-lg font-semibold"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            View All Certificates
            <ChevronRight className="ml-2" size={20} />
          </motion.a>
        </div>
      </div>

      <AnimatePresence>
        {selectedItem && (
          <motion.div 
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div 
              className="bg-gray-800 p-4 sm:p-6 rounded-lg w-full max-w-xs sm:max-w-sm md:max-w-md relative"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
            >
              <button 
                className="absolute top-2 right-2 text-gray-400 hover:text-white"
                onClick={() => setSelectedItem(null)}
              >
                <X size={20} />
              </button>
              <div className="flex flex-col sm:flex-row items-center mb-4">
                <div className="w-full sm:w-1/3 mb-4 sm:mb-0">
                  <img 
                    src={selectedItem.image} 
                    alt={selectedItem.name} 
                    className={`w-full h-auto object-cover ${selectedItem.type === 'badge' ? 'rounded-full' : 'rounded-lg'}`}
                  />
                </div>
                <div className="w-full sm:w-2/3 sm:pl-4 text-center sm:text-left">
                  <h3 className="text-lg text-white font-bold sm:text-xl  mb-2">{selectedItem.name}</h3>
                  <p className="text-gray-400 mb-1">{selectedItem.issuer}</p>
                  {selectedItem.date && (
                    <p className="text-gray-500 text-sm mb-2">{selectedItem.date}</p>
                  )}
                </div>
              </div>
              <motion.a
                href={selectedItem.link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center sm:justify-start text-blue-400 hover:text-blue-300 mt-4 w-full sm:w-auto"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <ExternalLink size={16} className="mr-2" />
                View {selectedItem.type === 'badge' ? 'Badge' : 'Certificate'}
              </motion.a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default CertificatesBadgesCollage;